<template>
  <v-container fluid class="pa-7">
    <header-component :title="$vuetify.lang.t('$vuetify.navigation.check')"> </header-component>

    <v-alert v-if="isSuccessShown" border="left" color="green" dark>
      {{ $vuetify.lang.t(`$vuetify.check.success1`) }}
      <strong>{{ $route.params.id }}</strong>
      {{ $vuetify.lang.t(`$vuetify.check.success2`) }}
    </v-alert>

    <v-row>
      <v-col cols="lg-4" offset-lg="4">
        <v-progress-circular v-if="searchPending && !REQUEST_STATUS.FAIL" indeterminate color="primary" class="my-4" />

        <v-text-field
          :label="$vuetify.lang.t('$vuetify.search.orderById')"
          type="number"
          ref="mainInput"
          outlined
          single-line
          hide-details
          v-model="searchQuery"
          @input="search"
          clearable
        />

        <p v-if="status === REQUEST_STATUS.FAIL" class="color-red mt-2">
          {{ $vuetify.lang.t(`$vuetify.check.badId`) }}
        </p>

        <p v-if="badStatus" class="color-red mt-2">
          {{ $vuetify.lang.t(`$vuetify.check.badStatus`) }}
        </p>

        <div class="d-flex mt-4 w-100">
          <QrCodeReaderComponent @decode="decode($event)" :is-order="false" width="100%" height="56" class="w-100" />
        </div>
      </v-col>
    </v-row>

    <!--  ORDER STATE UPDATE dialog  -->
    <v-dialog v-model="orderCredits.isDialogShown" max-width="1420" content-class="h-100">
      <v-card class="d-flex flex-column h-100">
        <AdminViewComponent
          class="h-100"
          :admin-endpoint="`/order-credits/detail/${lastOrderCredit && lastOrderCredit.id}`"
        />

        <v-card-actions class="py-4">
          <v-btn @click="orderCredits.isDialogShown = false" color="grey lighten-2">
            {{ $vuetify.lang.t('$vuetify.basic.cancel') }}
          </v-btn>
          <v-spacer />
          <v-btn @click="updateOrderCreditState" color="success" :loading="orderCredits.isUpdating">
            {{ $vuetify.lang.t('$vuetify.basic.confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import HeaderComponent from '../../components/layout/HeaderComponent.vue';
import QrCodeReaderComponent from '../../components/global/QrCodeReaderComponent.vue';
import REQUEST_STATUS from '../../common/enums/requestStatus';
import ORDER_STATE from '../../common/enums/orderState';
import { OrderCreditModel } from '../../common/models/order-credit.model';
import { OrderCreditsApiService } from '../../common/services/order-credits.api.service';
import AdminViewComponent from '../../components/global/AdminViewComponent.vue';
import { OrderCreditState } from '../../enums/order-credits';
import { ORDER_ID_LENGTH } from '../../constants/orderId';

export default {
  components: {
    AdminViewComponent,
    HeaderComponent,
    QrCodeReaderComponent,
  },

  data() {
    return {
      REQUEST_STATUS,
      ORDER_STATE,
      searchQuery: null,
      searchedId: null,
      searchPending: false,
      dialog: false,
      badStatus: false,
      orderCredits: {
        isDialogShown: false,
        isUpdating: false,
      },
    };
  },

  computed: {
    ...mapGetters({
      user: 'auth/getUser',
      status: 'stores/getStatus',
      getOrder: 'stores/getOrder',
      // `/api/theme/stores/orders-quality-assurance` response
      getOrderItem: 'stores/getItem',
      getEnumeratorsModel: 'orderCredits/getEnums',
    }),

    /**
     * Whether the user was redirected to this page from the order detail page
     * after a successful review.
     * @returns { boolean }
     */
    isSuccessShown() {
      return !!this.$route.params.id;
    },

    wasFlawedItemPresentInPreviousReview() {
      return !!this.orderCreditId;
    },

    /**
     * The last order credit that was created after a successful review.
     * The order credit data is received from a POST request in the `views/check/Detail.vue` component
     * and is passed to this component via vuex.
     *
     * ! That is why this is null if the user was not redirected from the order detail page after a successful review.
     * @returns { OrderCreditModel | null}
     */
    lastOrderCredit() {
      if (
        !this.isSuccessShown ||
        !this.getOrderItem ||
        !this.getOrderItem.order_credits ||
        !this.getOrderItem.order_credits.length
      )
        return null;

      return new OrderCreditModel(
        this.getOrderItem.order_credits[this.getOrderItem.order_credits.length - 1].order_credit
      );
    },
  },

  methods: {
    ...mapActions({
      fetchOrderItems: 'stores/fetchOrderItems',
    }),

    decode(e) {
      setTimeout(() => {
        this.searchQuery = e;
        this.$forceUpdate();
      }, 100);
    },

    async search() {
      // warn about the order id being too long
      if (this.searchQuery.length > ORDER_ID_LENGTH) {
        this.$alertWarning(this.$vuetify.lang.t('$vuetify.messages.order_id_too_long'));
        return;
      }
      // if the order id does not match the expected order id length, do nothing
      if (this.searchQuery.length !== ORDER_ID_LENGTH) {
        return;
      }

      try {
        await this.fetchOrderItems({ url: 'orders-quality-assurance/' + this.searchQuery });

        if (this.getOrder.order.order_state_id !== ORDER_STATE.CHECKING) {
          this.badStatus = true;

          return;
        }

        if (this.status === REQUEST_STATUS.SUCCESS) {
          this.$router.push({ name: 'check.detail', params: { id: this.searchQuery } });
        }
      } catch (error) {
        this.$alertError(this.$vuetify.lang.t('$vuetify.messages.ordersQualityAssuranceFetchError'));
      }
    },

    /**
     * Sets the order credit state to `valid`.
     * Used in the order credit dialog that appears after a successful review.
     * @returns {Promise<void>}
     */
    async updateOrderCreditState() {
      // if there's no last order credit, error out and return - shouldn't happen
      if (!this.lastOrderCredit) {
        this.$alertError(this.$vuetify.lang.t('$vuetify.errors.no_active_order_credit'));
        return;
      }

      // start loading
      this.$set(this.orderCredits, 'isUpdating', true);

      try {
        // update the order credit state to VALID
        await OrderCreditsApiService.patch(this.lastOrderCredit.id, {
          state: OrderCreditState.VALID,
        });

        // close the dialog
        this.$set(this.orderCredits, 'isDialogShown', false);

        // alert success
        this.$alertSuccess(this.$vuetify.lang.t('$vuetify.messages.order_credit_state_set_to_valid'));
      } catch (error) {
        this.$alertError(this.$vuetify.lang.t('$vuetify.errors.order_credit_state_update_failed'));
      } finally {
        // stop loading
        this.$set(this.orderCredits, 'isUpdating', false);
      }
    },
  },

  async created() {
    // if there was an order credit created after the review, show the dialog to update its state
    if (this.lastOrderCredit) {
      this.$set(this.orderCredits, 'isDialogShown', true);
    }
  },

  mounted() {
    this.$refs.mainInput.focus();
  },
};
</script>
